import { unreachableCaseError } from 'types';
import prop from 'ramda/es/prop';
import uniqBy from 'ramda/es/uniqBy';
import { nanoid } from 'nanoid';
import { getCountdownSeconds, getExpectedSentAtFromSubmittedAt } from 'utils/report';
import {
  worklistItemToSlateContent,
  getDefaultSlateFieldChildren,
  generateAddendumSlateContentForAddendum,
  createAddendumFieldName,
} from './utils';
import { createParagraph } from '../../RichTextEditor/plugins/paragraph/utils';
import type { SlateContent } from '../types';
import type { ReportMacros, LoadedReportWorklistItem } from 'hooks/useCurrentCaseReport';

export const ADDENDUM_NAME_PLACEHOLDER = 'New Addendum';

export type Addendum = {
  content: SlateContent;
  name: string;
  ownerFirstName?: Nullable<string>;
  ownerLastName?: Nullable<string>;
  submittedAt?: Nullable<Date>;
};

export type FieldsState = {
  reportID: string;
  initialStateReconciled: boolean;
  reporterToolbarMountPoint: Nullable<HTMLElement>;
  worklistItem: LoadedReportWorklistItem;
  previousWorklistItemRef: {
    current: Nullable<LoadedReportWorklistItem>;
  };
  /**
   * Report content supplied by lifecycle initialization. Live report-body edits are stored in
   * FieldsProvider's ref and must be read through FieldsDispatch.getCurrentReportContent().
   */
  content: SlateContent;
  finalizedAddendums: Addendum[];
  addendum: Nullable<Addendum>;
  addendumSMID: Nullable<string>;
  addendumSubmittedDate: Nullable<Date>;
  editorKey: string;
  macros: ReportMacros;
  /**
   * When loading the worklist item, the backend returns the default template in the response.
   * `activateTemplateID` is the id for that template that is applied by default when the user
   * opens the report, and also serves as the template id for any subsequent template insertions.
   */
  activateTemplateID?: string;
  touched?: boolean;
};

export const initFieldsState = ({
  worklistItem,
  previousWorklistItemRef,
  reporterToolbarMountPoint,
}: Readonly<{
  worklistItem: LoadedReportWorklistItem;
  previousWorklistItemRef: {
    current: Nullable<LoadedReportWorklistItem>;
  };
  reporterToolbarMountPoint: Nullable<HTMLElement>;
}>) => ({
  reportID: '',
  initialStateReconciled: false,
  addendum: undefined,
  addendumSMID: undefined,
  addendumSubmittedDate: undefined,
  editorKey: nanoid(),
  reporterToolbarMountPoint,
  worklistItem,
  previousWorklistItemRef,
  content: worklistItemToSlateContent(worklistItem),
  macros: uniqBy(prop('smid'), [
    ...(worklistItem?.report?.template?.macros ?? []),
    ...(worklistItem?.report?.macros ?? []),
  ]),

  activateTemplateID: worklistItem?.report?.template?.id,
  finalizedAddendums:
    worklistItem?.report?.addendums
      ?.filter(({ submittedAt, sentAt: sentToMirthAt }) => {
        const addendumSecondsFromServer = getCountdownSeconds(
          getExpectedSentAtFromSubmittedAt(submittedAt)
        );
        return (
          (addendumSecondsFromServer != null && addendumSecondsFromServer < 0) ||
          sentToMirthAt !== null
        );
      })
      .map((addendum: any, index: any) => ({
        name: createAddendumFieldName({
          lastName: addendum.owner?.lastName,
          addendumNumber: index + 1,
          createdDate: addendum.created,
        }),
        content: generateAddendumSlateContentForAddendum(addendum) ?? [],
        ownerFirstName: addendum.owner?.firstName,
        ownerLastName: addendum.owner?.lastName,
        submittedAt: addendum.submittedAt ?? addendum.created,
      })) ?? [],
});

export type ActionType =
  | {
      type: 'initFieldsState';
      payload: {
        worklistItem: LoadedReportWorklistItem;
        previousWorklistItemRef: {
          current: Nullable<LoadedReportWorklistItem>;
        };
        reporterToolbarMountPoint: HTMLElement;
      };
    }
  | {
      type: 'markInitialStateReconciled';
    }
  | {
      type: 'setReporterToolbarMountPoint';
      payload: {
        reporterToolbarMountPoint: HTMLElement;
      };
    }
  | {
      type: 'insertTemplate';
      payload: {
        macros: ReportMacros;
        activateTemplateID: string;
      };
    }
  | {
      type: 'addAddendum';
    }
  | {
      type: 'addAddendumSMID';
      payload: {
        addendumSMID: string;
      };
    }
  | {
      type: 'submitAddendum';
      payload: {
        addendumSubmittedDate: Nullable<Date>;
      };
    }
  | {
      type: 'updateAddendumContent';
      payload: {
        addendumContent: SlateContent;
      };
    }
  | {
      type: 'restoreAddendum';
      payload: {
        addendum: Addendum;
        addendumSMID: string;
        addendumSubmittedDate: Nullable<Date>;
      };
    }
  | {
      type: 'clearAddAddendum';
    }
  | {
      type: 'finalizeProvisionalAddendum';
      payload: {
        submittingUserFirstName: Nullable<string>;
        submittingUserLastName: Nullable<string>;
        addendumSubmittedDate?: Nullable<Date>;
      };
    };

export const fieldsReducer = (state: FieldsState, action: ActionType): FieldsState => {
  switch (action.type) {
    case 'initFieldsState':
      return initFieldsState({
        ...action.payload,
      });
    case 'markInitialStateReconciled':
      return { ...state, initialStateReconciled: true };
    case 'setReporterToolbarMountPoint':
      return { ...state, ...action.payload };
    case 'insertTemplate':
      return {
        ...state,
        ...action.payload,
      };
    case 'addAddendum':
      return {
        ...state,
        addendum: {
          name: ADDENDUM_NAME_PLACEHOLDER,
          content: getDefaultSlateFieldChildren(),
        },
      };
    case 'restoreAddendum':
      let addendumContent = action.payload.addendum?.content;
      // We expect addendum content. If it's empty, it's likely an internal test case.
      if (action.payload.addendum?.content.length !== 0) {
        addendumContent =
          action.payload.addendum?.content.__typename === 'AddendumSironaSlate'
            ? action.payload.addendum?.content.sironaSlate
            : action.payload.addendum?.content.textBlob
                .split('\n')
                .map((text: any) => createParagraph(text));
      }
      return {
        ...state,
        addendumSMID: action.payload.addendumSMID,
        addendumSubmittedDate: action.payload.addendumSubmittedDate,
        addendum: {
          name: action.payload.addendum?.name ?? ADDENDUM_NAME_PLACEHOLDER,
          content: addendumContent,
        },
      };
    case 'updateAddendumContent':
      return {
        ...state,
        addendum: {
          name: state.addendum?.name ?? ADDENDUM_NAME_PLACEHOLDER,
          content: action.payload.addendumContent,
        },
      };
    case 'addAddendumSMID':
      return { ...state, ...action.payload };
    case 'submitAddendum':
      return { ...state, ...action.payload };
    case 'clearAddAddendum':
      return { ...state, addendumSMID: undefined, addendum: undefined };
    case 'finalizeProvisionalAddendum':
      return {
        ...state,
        finalizedAddendums: [
          ...state.finalizedAddendums,
          {
            name: createAddendumFieldName({
              addendumNumber: state.finalizedAddendums.length + 1,
              lastName: action.payload.submittingUserLastName,
              createdDate: state.addendumSubmittedDate ?? action.payload.addendumSubmittedDate,
            }),
            content: state.addendum?.content,
            ownerFirstName: action.payload.submittingUserFirstName,
            ownerLastName: action.payload.submittingUserLastName,
            submittedAt: state.addendumSubmittedDate ?? action.payload.addendumSubmittedDate,
          },
        ],
        addendumSMID: undefined,
        addendum: undefined,
      };
    default:
      unreachableCaseError((action as unknown as ActionType).type);
  }

  return state;
};
